import * as THREE from 'three';
import { offscreen } from './platform.js';
import { resizeTextureCanvas } from './canvasTexture.js';
import { explorationLayout } from './exploration.js';
import { insideRect, mobileLayout } from './mobileLayout.js';
import { touchPadLayout } from './touchControls.js';
import { pill, UI_COLORS } from './uiArt.js';

const overlaps = (a, b, gap = 0) => Boolean(b && a.x < b.x + b.width + gap && a.x + a.width + gap > b.x
  && a.y < b.y + b.height + gap && a.y + a.height + gap > b.y);

export function explorationCameraLayout(screen, { navRect, statusRect, controlsRect } = {}) {
  const layout = mobileLayout(screen), safe = layout.safe;
  const nav = navRect || layout.nav;
  const touch = Boolean(screen.isTouch || screen.isWeChat);
  const widths = touch ? [80, 64] : [88, 80];
  const width = widths[0] + widths[1] + 8;
  const group = { x: screen.width - safe.right - width - 12, y: safe.top + 66, width, height: 44 };
  if (group.x < safe.left + 8) return [];
  const floating = [statusRect || layout.status, explorationLayout(screen, { statusRect }).compass];
  for (let pass = 0; pass < floating.length; pass++) {
    for (const blocker of floating) if (overlaps(group, blocker, 6)) group.y = blocker.y + blocker.height + 8;
  }
  const bottomControls = [nav, controlsRect];
  if (touch) bottomControls.push(...Object.values(touchPadLayout(screen, nav)));
  if (group.y + group.height > screen.height - safe.bottom - 8
    || bottomControls.some(rect => overlaps(group, rect, 8))) return [];
  return [
    { id: 'toggle', x: group.x, y: group.y, width: widths[0], height: 44 },
    { id: 'recenter', x: group.x + widths[0] + 8, y: group.y, width: widths[1], height: 44 },
  ];
}

export function createExplorationCameraUi(hudScene, { onToggle, onRecenter } = {}) {
  const items = ['toggle', 'recenter'].map(id => {
    const canvas = offscreen(176, 88), texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, depthTest: false, depthWrite: false, transparent: true }));
    sprite.center.set(0, 1); sprite.renderOrder = 6; sprite.visible = false; hudScene.add(sprite);
    return { id, canvas, texture, sprite, paint: '' };
  });
  let rects = [];
  return {
    get rects() { return rects.map(rect => ({ ...rect })); },
    update(screen, { mode = 'follow', visible = true, navRect, statusRect, controlsRect } = {}) {
      rects = visible ? explorationCameraLayout(screen, { navRect, statusRect, controlsRect }) : [];
      const touch = Boolean(screen.isTouch || screen.isWeChat);
      for (const item of items) {
        const rect = rects.find(rect => rect.id === item.id);
        item.sprite.visible = Boolean(rect);
        if (!rect) continue;
        item.sprite.position.set(rect.x, screen.height - rect.y, .5);
        item.sprite.scale.set(rect.width, rect.height, 1);
        const label = item.id === 'toggle' ? mode === 'overview' ? '总览' : '探索' : '回正';
        const paint = JSON.stringify([label, touch, rect.width, rect.height]);
        if (paint === item.paint) continue;
        item.paint = paint;
        resizeTextureCanvas(item.canvas, item.texture, rect.width * 2, rect.height * 2);
        const ctx = item.canvas.getContext('2d');
        ctx.setTransform(2, 0, 0, 2, 0, 0); ctx.clearRect(0, 0, rect.width, rect.height);
        ctx.fillStyle = UI_COLORS.panel; ctx.strokeStyle = item.id === 'toggle' && mode !== 'overview' ? UI_COLORS.gold : 'rgba(226,191,121,.58)';
        ctx.lineWidth = 1; pill(ctx, .5, .5, rect.width - 1, rect.height - 1, 13); ctx.fill(); ctx.stroke();
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillStyle = UI_COLORS.text; ctx.font = '500 14px sans-serif';
        ctx.fillText(label, rect.width / 2 + (touch ? 0 : 11), rect.height / 2);
        if (!touch) {
          ctx.fillStyle = 'rgba(226,191,121,.15)'; pill(ctx, 9, 13, 20, 18, 4); ctx.fill();
          ctx.fillStyle = UI_COLORS.gold; ctx.font = '600 11px sans-serif';
          ctx.fillText(item.id === 'toggle' ? 'C' : 'R', 19, 22);
        }
        item.texture.needsUpdate = true;
      }
    },
    contains(x, y) { return rects.some(rect => insideRect(rect, x, y)); },
    tap(x, y) {
      const rect = rects.find(rect => insideRect(rect, x, y));
      if (!rect) return false;
      if (rect.id === 'toggle') onToggle?.();
      else onRecenter?.();
      return true;
    },
  };
}
